/**
 * French in an English delivery stream.
 *
 * Ruling 81 of 17 October 2026 (a): "An English document carries no French: no
 * word, heading or phrase."
 *
 * Section 8 of that block asks for a test that fails if French reaches the
 * English delivery stream, and for a statement of what the test catches and
 * what it cannot. Both are below, because a guard whose reach is not written
 * down gets trusted past it.
 *
 * **What it catches.** A locked phrase; a French letter; and a French word
 * that is not also an English word. "Actions sur place" is caught on `sur`
 * and on the phrase.
 *
 * **What it cannot catch.** A French phrase built only from words that are
 * also English words, because `en`, `on`, `ne`, `pas`, `que`, `la` and `se`
 * all appear in English or as abbreviations in these order sets — `EN` is an
 * enemy abbreviation in this very corpus, so a French-only set is the only
 * safe one. It cannot catch a French proper noun, a French place name, or an
 * English sentence that is simply wrong. It is a tripwire on the forms seen
 * so far, not a language detector: `detectLanguage` needs twenty-five words
 * and a stopword share, and a spoken segment is often four words long.
 */

/** Phrases ruled on or seen in this material. Matched case-insensitively. */
export const FRENCH_PHRASES: readonly string[] = Object.freeze([
  "actions sur place",
  "sur place",
  "ordre d'avertissement",
  "orienter avec la carte",
  "mise en garde",
]);

/**
 * French words that are not also English words, and are not abbreviations in
 * these order sets.
 *
 * Deliberately smaller than the language detector's stopword set. Dropped
 * from it: `en`, `on`, `ne`, `pas`, `que`, `la`, `se`, `son`, `ce`, `est` and
 * `il` — each is an English word, a callsign or an abbreviation here, and a
 * guard that fires on `EN` would fire on every enemy line in the corpus.
 */
export const FRENCH_WORDS: readonly string[] = Object.freeze([
  "aux",
  "avec",
  "ces",
  "cette",
  "dans",
  "des",
  "du",
  "elle",
  "et",
  "être",
  "je",
  "les",
  "leur",
  "leurs",
  "mais",
  "nous",
  "par",
  "pour",
  "qui",
  "ses",
  "sont",
  "sur",
  "une",
  "vous",
]);

/** The letters English order sets do not use and French does. */
const FRENCH_LETTERS = /[àâäçéèêëîïôöùûüÿœæ]/i;

const WORD = /[A-Za-zà-ÿœæ']+/g;

export interface FrenchFinding {
  /** Which segment of the stream, counting from zero. */
  readonly index: number;
  /** What tripped the guard: a phrase, a word, or a letter. */
  readonly marker: string;
  readonly kind: "letter" | "phrase" | "word";
}

/**
 * Every French marker in a spoken stream, in order.
 *
 * Ruling 50: a finding carries the marker that fired and the segment's index,
 * never the segment.
 */
export function frenchInSpokenStream(
  stream: readonly string[],
): readonly FrenchFinding[] {
  const findings: FrenchFinding[] = [];
  const words = new Set(FRENCH_WORDS);
  for (const [index, segment] of stream.entries()) {
    const lowered = segment.toLowerCase();
    for (const phrase of FRENCH_PHRASES) {
      if (lowered.includes(phrase)) {
        findings.push({ index, kind: "phrase", marker: phrase });
      }
    }
    const letter = FRENCH_LETTERS.exec(segment);
    if (letter !== null) {
      findings.push({ index, kind: "letter", marker: letter[0] });
    }
    for (const token of segment.match(WORD) ?? []) {
      // An all-capitals token is an abbreviation in this material, never a
      // French word: `EN` is the enemy, `DU` would be a unit.
      if (token === token.toUpperCase() && token.length > 1) continue;
      if (words.has(token.toLowerCase())) {
        findings.push({ index, kind: "word", marker: token.toLowerCase() });
      }
    }
  }
  return findings;
}
